import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Typography, IconButton, Box, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RaisedButton from 'material-ui/RaisedButton';
import InnerContent from '../../components/PageStructure/InnerContent/InnerContent';

import PageContent from '../../components/PageStructure/PageContent/PageContent';
import PageHeader from '../../components/PageStructure/PageHeader/PageHeader';
import Table from '../../containers/common/tables/tableContainer';

import Spinner from '../../components/common/Spinner/Spinner';
import globals from '../../globals/communityGlobals';
import { referralByPatientIdTable } from '../../settings/constants';

import { formatNhs } from '../../utils/helpers';
import NonBlockingLoader from '../../components/common/Loading/NonBlockingLoader';
import { ExternalProviderType } from '../../enums';
import { ENABLE_SETTING_PATIENTS_AS_INACTIVE } from '../../settings/constants/appSettings';

const locale = globals.getArea('referrals');
const patientsLocale = globals.getArea('patients');

const styles = {
  padding: '0',
};

class PatientReferrals extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isPatientActive: null,
    };
  }

  componentDidMount = () => {
    const { match, getPatient, getAppSettings, enableSettingPatientsAsInactive } = this.props;
    getPatient(match.params.patientId);
    if (enableSettingPatientsAsInactive == null) {
      getAppSettings(ENABLE_SETTING_PATIENTS_AS_INACTIVE);
    }
  };

  getIsPatientActiveStatus = patient => {
    return this.state.isPatientActive === null ? patient.isActive : this.state.isPatientActive;
  };

  handleGoBack = () => {
    this.props.goBack();
  };

  syncReferrals = (pasNumber, patientId, nhsNumber) => {
    const referralData = {
      patientId,
      nhsNumber,
      pasNumber,
    };
    this.props.updateReferrals(referralData).then(() => {
      this.setState({ isPatientActive: this.props.isPatientActive });
    });
  };

  render() {
    const {
      navigate,
      patient,
      message,
      go,
      pageTitle,
      canAddReferrals,
      enableIntegrationMode,
      isFetching,
      isSynced,
      externalProviderType,
    } = this.props;

    const formattedErrorMsg = () => {
      return message.split(';').map(item => <p>{item}</p>);
    };

    const columns = [
      {
        Header: locale.getMsg('orgUnit'),
        id: 'team',
        accessor: row => (row.team ? row.team : '-'),
      },
      {
        Header: locale.getMsg('referenceCode'),
        id: 'referenceCode',
        accessor: row => row.referenceCode,
      },
      {
        id: 'dateReceived',
        Header: locale.getMsg('referralReceived'),
        accessor: row => moment(row.dateReceived).format(globals.dateFormats.short),
      },
      {
        Header: locale.getMsg('status'),
        id: 'status',
        accessor: row => locale.getMsg(row.status),
      },
      {
        Header: locale.getMsg('service'),
        id: 'serviceName',
        accessor: row => row.serviceName,
      },
      {
        Header: locale.getMsg('source'),
        id: 'source',
        accessor: row => row.source,
        show: enableIntegrationMode,
      },
    ];

    return (
      <PageContent pageTitle={pageTitle}>
        <div className="flex-page-content content-wrapper">
          <PageHeader>
            <Box className="title-section" display="flex" alignItems="center" px={2} py={1}>
              <Box>
                <Tooltip title={locale.getMsg('back')}>
                  <IconButton
                    className="back-btn"
                    aria-label="back"
                    size="small"
                    color="primary"
                    onClick={this.handleGoBack}
                  >
                    <ArrowBackIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box flexGrow={1}>
                <Typography variant="h3">{locale.getMsg('createPageHeader')}</Typography>
                <Typography variant="h6">
                  <span>
                    {patient &&
                      `${patient.forename} ${patient.surname} -
                    ${patientsLocale.getMsg('nhsNumber')}: ${formatNhs(patient.nhsNumber)}`}
                  </span>
                  {patient && patient.importedByExternalProvider ? <span>&nbsp;(imported)</span> : ''}
                  {patient &&
                  this.props.enableSettingPatientsAsInactive === 'true' &&
                  !this.getIsPatientActiveStatus(patient) ? (
                    <span style={{ color: 'red', fontStyle: 'italic' }}>&nbsp;-&nbsp;Inactive</span>
                  ) : (
                    ''
                  )}
                </Typography>
              </Box>
              <Box>
                {patient && (
                  <div>
                    <span style={{ margin: '4px' }}>
                      {canAddReferrals &&
                      (!enableIntegrationMode ||
                        (enableIntegrationMode && externalProviderType === ExternalProviderType.Emis)) ? (
                        <RaisedButton
                          primary
                          onClick={() => go(`/Patients/${patient.id}/Referrals/Create`)}
                          label={locale.getMsg('createReferral')}
                        />
                      ) : (
                        ''
                      )}
                      {enableIntegrationMode &&
                      patient.importedByExternalProvider &&
                      externalProviderType !== ExternalProviderType.Emis ? (
                        <RaisedButton
                          primary
                          onClick={() => this.syncReferrals(patient.pasNumber, patient.id, patient.nhsNumber)}
                          label={locale.getMsg('syncReferrals')}
                          disabled={isFetching}
                        />
                      ) : (
                        ''
                      )}
                    </span>
                  </div>
                )}
              </Box>
            </Box>
          </PageHeader>
          <InnerContent style={styles} isBorderless>
            <Box display="flex" px={2} py={1}>
              <Box flexGrow={1}>
                {patient ? (
                  <Table
                    triggerReload={isSynced === true}
                    endpoint={referralByPatientIdTable}
                    reduxIdentifier="patientReferrals"
                    responseObjectName="referrals"
                    columns={columns}
                    initialSearchParams={{
                      patientId: patient.id,
                      pageNumber: 1,
                      pageSize: 30,
                    }}
                    rowOnClick={rowInfo => {
                      navigate(`/Patients/Patient/Referrals/${rowInfo.original.id}`);
                    }}
                    noDataText={locale.getMsg('noReferralsInTable')}
                    noDataComponent={<strong>{locale.getMsg('noReferralsInTable')}</strong>}
                    afterReloadCallback={setTimeout(() => this.props.resetSyncStatus(), 5000)}
                  />
                ) : (
                  <Spinner />
                )}
              </Box>
            </Box>
          </InnerContent>
        </div>
        {isSynced && <NonBlockingLoader message={formattedErrorMsg(message)} autohideDuration={5000} />}
      </PageContent>
    );
  }
}

PatientReferrals.propTypes = {
  navigate: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      patientId: PropTypes.string.isRequired,
    }),
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  patient: PropTypes.shape({
    address: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      postCode: PropTypes.string,
    }),
    patientFullName: PropTypes.string,
  }),
  go: PropTypes.func.isRequired,
  getPatient: PropTypes.func.isRequired,
  pageTitle: PropTypes.string.isRequired,
  canAddReferrals: PropTypes.bool.isRequired,
  enableIntegrationMode: PropTypes.bool.isRequired,
};

PatientReferrals.defaultProps = {
  patient: null,
};

export default PatientReferrals;
